'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@/comps/primitives/button';
import { Dialog, DialogContent, DialogContentWrapper } from '@/comps/primitives/dialog';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { formatProgramModalSubtitle, getUpskillingProgramTemplate } from '@/domains/pages/ai-proficiency/fixtures/upskilling-program-templates';
import { buildProgramEmailPreviewHtml } from '@/domains/pages/ai-proficiency/utils/build-program-email-html';
import { copyProgramEmailToClipboard } from '@/domains/pages/ai-proficiency/utils/copy-program-email';
import { COPY_TEXT_TIMEOUT } from '@/hooks/use-copy-to-clipboard';
import { Check, Copy, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
export type CreateProgramModalContext = {
  level: AifiLevel;
  dimensionKey: string;
  dimensionLabel: string;
};
type CreateProgramModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: CreateProgramModalContext | null;
};
export function CreateProgramModal(t0) {
  const $ = _c(42);
  const {
    open,
    onOpenChange,
    context
  } = t0;
  const [recentlyCopied, setRecentlyCopied] = useState(false);
  let t1;
  if ($[0] !== context) {
    t1 = context ? getUpskillingProgramTemplate(context.dimensionKey, context.level) : null;
    $[0] = context;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const template = t1;
  let t2;
  if ($[2] !== template) {
    t2 = template ? buildProgramEmailPreviewHtml(template) : "";
    $[2] = template;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const emailHtml = t2;
  let t3;
  let t4;
  if ($[4] !== open) {
    t3 = () => {
      if (!open) {
        setRecentlyCopied(false);
      }
    };
    t4 = [open];
    $[4] = open;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== template) {
    t5 = async () => {
      if (!template) {
        return;
      }
      try {
        await copyProgramEmailToClipboard(template);
        setRecentlyCopied(true);
        window.setTimeout(() => setRecentlyCopied(false), COPY_TEXT_TIMEOUT);
      } catch {}
    };
    $[7] = template;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleCopy = t5;
  if (!context || !template) {
    return null;
  }
  let t6;
  if ($[9] !== context.dimensionLabel || $[10] !== context.level) {
    t6 = formatProgramModalSubtitle(context.dimensionLabel, context.level);
    $[9] = context.dimensionLabel;
    $[10] = context.level;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const subtitle = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="text-lg font-semibold text-foreground">Create program</h2>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== subtitle) {
    t8 = <div className="min-w-0">{t7}<p className="mt-1 text-sm text-muted-foreground">{subtitle}</p></div>;
    $[13] = subtitle;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== recentlyCopied) {
    t9 = recentlyCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />;
    $[15] = recentlyCopied;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = recentlyCopied ? "Copied" : "Copy text";
  let t11;
  if ($[17] !== handleCopy || $[18] !== t10 || $[19] !== t9) {
    t11 = <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 rounded-md px-3 text-xs" onClick={handleCopy}>{t9}{t10}</Button>;
    $[17] = handleCopy;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== onOpenChange) {
    t12 = () => onOpenChange(false);
    $[21] = onOpenChange;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <X className="size-4" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12) {
    t14 = <button type="button" className="rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground" onClick={t12} aria-label="Close">{t13}</button>;
    $[24] = t12;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t14) {
    t15 = <div className="flex shrink-0 items-center gap-2">{t11}{t14}</div>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t8) {
    t16 = <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">{t8}{t15}</div>;
    $[29] = t15;
    $[30] = t8;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">Email preview</p>;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== emailHtml) {
    t18 = <DialogContent className="gap-0 px-6 py-5">{t17}<div className="mt-3 max-h-[min(70vh,720px)] max-w-160 overflow-y-auto rounded-lg border border-border"><div className="mx-auto max-w-160 overflow-hidden" data-testid="create-program-email-preview" dangerouslySetInnerHTML={{
          __html: emailHtml
        }} /></div></DialogContent>;
    $[33] = emailHtml;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t16 || $[36] !== t18) {
    t19 = <DialogContentWrapper size="lg" className="max-w-2xl gap-0 overflow-hidden p-0" onClick={_temp}>{t16}{t18}</DialogContentWrapper>;
    $[35] = t16;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== onOpenChange || $[39] !== open || $[40] !== t19) {
    t20 = <Dialog open={open} onOpenChange={onOpenChange}>{t19}</Dialog>;
    $[38] = onOpenChange;
    $[39] = open;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
}
function _temp(event) {
  return event.stopPropagation();
}